import { methodologyCellProps } from 'components/ProjectMethodology';
import { getStageLabel } from 'components/ProjectStage';
import { getStatusLabel } from 'components/ProjectStatus/ProjectStatus';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    ProjectContractTypeEnum,
    ProjectData,
    ProjectMethodologyEnum,
    ProjectStageEnum,
    ProjectStatusEnum,
} from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { getContractTypeLabel } from 'types/Contract';
import { Informer, Loader } from 'uikit';

import { useParams } from 'react-router-dom';

import { ProjectPageLayout } from '../Layouts/ProjectPageLayout';

import { ProjectUpdateForm } from './ProjectUpdateForm';

export interface IProjectData
    extends Omit<
        ProjectData,
        | 'contractType'
        | 'methodology'
        | 'stage'
        | 'status'
        | 'subsidiary'
        | 'statusFollowers'
        | 'createdAt'
        | 'lotPrice'
        | 'actualPrice'
    > {
    methodology: { id: ProjectMethodologyEnum; label: string };
    stage: { id: ProjectStageEnum; label: string };
    status: { id: ProjectStatusEnum; label: string };
    createdAt: string;
    statusFollowers: { id: ProjectStatusEnum; label: string }[];
    subsidiary?: { id: number; label: string } | null;
    lotPrice?: string | null;
    actualPrice?: string | null;
}

const projectDataToDefaultValues = (projectData?: ProjectData) => {
    if (!projectData) {
        return projectData;
    }

    const subsidiary = projectData?.subsidiary
        ? {
              id: projectData?.subsidiary?.id,
              label: projectData?.subsidiary?.name,
          }
        : undefined;

    return {
        ...projectData,
        methodology: {
            id: projectData.methodology,
            label: methodologyCellProps[projectData.methodology].label,
        },
        stage: {
            id: projectData.stage,
            label: getStageLabel(projectData.stage),
        },
        status: {
            id: projectData?.status,
            label: getStatusLabel(projectData?.status),
        },
        subsidiary,
        statusFollowers: projectData?.statusFollowers?.map((v) => ({ id: v, label: getStatusLabel(v) })),
        createdAt: new Date(projectData.createdAt).toLocaleDateString(),
        lotPrice: Number.isFinite(projectData?.contractPrice?.data?.contractPlan)
            ? projectData?.contractPrice?.data?.contractPlan?.toFixed(2)
            : projectData?.contractPrice?.data?.contractPlan,
        actualPrice: Number.isFinite(projectData?.contractPrice?.data?.actualCost)
            ? projectData?.contractPrice?.data?.actualCost?.toFixed(2)
            : projectData?.contractPrice?.data?.actualCost,
        priceNMC: Number.isFinite(projectData?.calculationPrice?.data?.priceNMC)
            ? projectData?.calculationPrice?.data?.priceNMC?.toFixed(2)
            : projectData?.calculationPrice?.data?.priceNMC,
        contractType: {
            id: projectData?.contractType,
            label: getContractTypeLabel(projectData?.contractType as ProjectContractTypeEnum),
        },
    };
};

export const ProjectUpdate = () => {
    const { id } = useParams();

    const { projectData, projectDataStatus, projectDataError } = useProjectData(id);

    return (
        <>
            {projectDataStatus === RequestStatus.SUCCESS && projectData && (
                <ProjectPageLayout title={projectData.name}>
                    <ProjectUpdateForm
                        defaultValues={projectDataToDefaultValues(projectData)}
                        statusProject={projectData?.status}
                    />
                </ProjectPageLayout>
            )}
            {projectDataStatus === RequestStatus.FETCHING && <Loader />}
            {projectDataStatus === RequestStatus.ERROR && (
                <Informer status="alert" view="filled" title="Ошибка запроса" label={projectDataError} />
            )}
        </>
    );
};

ProjectUpdate.displayName = 'ProjectUpdate';
